/**
 * proveedor-documento.ts — documents of a supplier (migration 0043, 2026-10-27, user: "que se pueda agregar
 * documentacion al proveedor. Tipo de documento de proveedor - Libre de Deuda, Contrato").
 *
 *   GET    /sumi/cuentas-pagar/proveedores/:id/documentos            → ProveedorDocumentosResponse
 *   POST   /sumi/cuentas-pagar/proveedores/:id/documentos            ProveedorDocumentoCreate → ProveedorDocumento (201)
 *   DELETE /sumi/cuentas-pagar/proveedores/:id/documentos/:docId     → 204
 *
 * A "libre_deuda" without `vence`, or with `vence` today or later, is what the invoice grid shows as "Libre de deuda: Sí".
 */
import { z } from 'zod';
import { IsoDateSchema } from './vocab.js';

export const PROVEEDOR_DOCUMENTO_TIPOS = ['libre_deuda', 'contrato'] as const;
export const ProveedorDocumentoTipoSchema = z.enum(PROVEEDOR_DOCUMENTO_TIPOS);
export type ProveedorDocumentoTipo = z.infer<typeof ProveedorDocumentoTipoSchema>;
export const PROVEEDOR_DOCUMENTO_LABELS: Readonly<Record<ProveedorDocumentoTipo, string>> = {
  libre_deuda: 'Libre de deuda',
  contrato: 'Contrato',
};

export const ProveedorDocumentoCreateSchema = z.object({
  tipo: ProveedorDocumentoTipoSchema,
  /** The uploaded file (`POST /files`). */
  fileId: z.string().uuid(),
  nombreArchivo: z.string().trim().min(1).max(255),
  /** Expiry date; omitted = it does not expire. */
  vence: IsoDateSchema.optional(),
});
export type ProveedorDocumentoCreate = z.infer<typeof ProveedorDocumentoCreateSchema>;

export const ProveedorDocumentoSchema = z.object({
  id: z.string().uuid(),
  tipo: ProveedorDocumentoTipoSchema,
  fileId: z.string().uuid(),
  nombreArchivo: z.string(),
  vence: z.string().nullable(),
  createdAt: z.string(),
});
export type ProveedorDocumento = z.infer<typeof ProveedorDocumentoSchema>;

export const ProveedorDocumentosResponseSchema = z.object({ items: z.array(ProveedorDocumentoSchema) });
export type ProveedorDocumentosResponse = z.infer<typeof ProveedorDocumentosResponseSchema>;

/** A libre de deuda counts while it has no expiry or expires `hoy` or later (`hoy` = AAAA-MM-DD). */
export function libreDeudaVigente(documentos: readonly { tipo: string; vence: string | null }[], hoy: string): boolean {
  return documentos.some((d) => d.tipo === 'libre_deuda' && (d.vence === null || d.vence.slice(0, 10) >= hoy));
}
